import { create } from 'zustand';
import {
  medicalHistoryApi,
  MedicalHistory,
  MedicalHistorySummary,
  ChronicDisease,
  Allergy,
  Surgery,
  FamilyHistoryItem,
} from '../services/api/medicalHistoryApi';

interface MedicalHistoryStore {
  // State
  medicalHistory: MedicalHistory | null;
  summary: MedicalHistorySummary | null;
  isLoading: boolean;
  error: string | null;
  currentPatientId: string | null;

  // Actions
  fetchMedicalHistory: (patientId: string) => Promise<void>;
  fetchSummary: (patientId: string) => Promise<void>;
  clearMedicalHistory: () => void;

  // Chronic diseases
  addChronicDisease: (disease: Omit<ChronicDisease, 'id'>) => Promise<void>;
  updateChronicDisease: (disease: ChronicDisease) => Promise<void>;
  removeChronicDisease: (diseaseId: string) => Promise<void>;

  // Allergies
  addAllergy: (allergy: Omit<Allergy, 'id'>) => Promise<void>;
  updateAllergy: (allergy: Allergy) => Promise<void>;
  removeAllergy: (allergyId: string) => Promise<void>;

  // Surgeries
  addSurgery: (surgery: Omit<Surgery, 'id'>) => Promise<void>;
  updateSurgery: (surgery: Surgery) => Promise<void>;
  removeSurgery: (surgeryId: string) => Promise<void>;

  // Family history
  addFamilyHistoryItem: (item: Omit<FamilyHistoryItem, 'id'>) => Promise<void>;
  updateFamilyHistoryItem: (item: FamilyHistoryItem) => Promise<void>;
  removeFamilyHistoryItem: (itemId: string) => Promise<void>;

  // Notes
  updateNotes: (notes: string) => Promise<void>;

  // Review
  markReviewed: () => Promise<void>;
}

export const useMedicalHistoryStore = create<MedicalHistoryStore>((set, get) => ({
  medicalHistory: null,
  summary: null,
  isLoading: false,
  error: null,
  currentPatientId: null,

  fetchMedicalHistory: async (patientId: string) => {
    set({ isLoading: true, error: null, currentPatientId: patientId });
    try {
      const medicalHistory = await medicalHistoryApi.getByPatientId(patientId);
      set({ medicalHistory, isLoading: false });
    } catch (error: unknown) {
      set({ error: (error as Error).message || 'Failed to fetch medical history', isLoading: false });
    }
  },

  fetchSummary: async (patientId: string) => {
    try {
      const summary = await medicalHistoryApi.getSummary(patientId);
      set({ summary });
    } catch (error: unknown) {
      console.error('Failed to fetch medical history summary:', error);
    }
  },

  clearMedicalHistory: () => {
    set({ medicalHistory: null, summary: null, currentPatientId: null, error: null });
  },

  // Chronic diseases
  addChronicDisease: async (disease) => {
    const { currentPatientId } = get();
    if (!currentPatientId) return;

    set({ isLoading: true, error: null });
    try {
      const updated = await medicalHistoryApi.addChronicDisease(currentPatientId, disease);
      set({ medicalHistory: updated, isLoading: false });
    } catch (error: unknown) {
      set({ error: (error as Error).message || 'Failed to add chronic disease', isLoading: false });
      throw error;
    }
  },

  updateChronicDisease: async (disease) => {
    const { medicalHistory, currentPatientId } = get();
    if (!medicalHistory || !currentPatientId) return;

    set({ isLoading: true, error: null });
    try {
      const updatedDiseases = medicalHistory.chronicDiseases.map((d) =>
        d.id === disease.id ? disease : d
      );
      const updated = await medicalHistoryApi.update(medicalHistory.id, {
        chronicDiseases: updatedDiseases,
      });
      set({ medicalHistory: updated, isLoading: false });
    } catch (error: unknown) {
      set({ error: (error as Error).message || 'Failed to update chronic disease', isLoading: false });
      throw error;
    }
  },

  removeChronicDisease: async (diseaseId) => {
    const { currentPatientId } = get();
    if (!currentPatientId) return;

    set({ isLoading: true, error: null });
    try {
      const updated = await medicalHistoryApi.removeChronicDisease(currentPatientId, diseaseId);
      set({ medicalHistory: updated, isLoading: false });
    } catch (error: unknown) {
      set({ error: (error as Error).message || 'Failed to remove chronic disease', isLoading: false });
      throw error;
    }
  },

  // Allergies
  addAllergy: async (allergy) => {
    const { currentPatientId } = get();
    if (!currentPatientId) return;

    set({ isLoading: true, error: null });
    try {
      const updated = await medicalHistoryApi.addAllergy(currentPatientId, allergy);
      set({ medicalHistory: updated, isLoading: false });
    } catch (error: unknown) {
      set({ error: (error as Error).message || 'Failed to add allergy', isLoading: false });
      throw error;
    }
  },

  updateAllergy: async (allergy) => {
    const { medicalHistory } = get();
    if (!medicalHistory) return;

    set({ isLoading: true, error: null });
    try {
      const updatedAllergies = medicalHistory.allergies.map((a) =>
        a.id === allergy.id ? allergy : a
      );
      const updated = await medicalHistoryApi.update(medicalHistory.id, {
        allergies: updatedAllergies,
      });
      set({ medicalHistory: updated, isLoading: false });
    } catch (error: unknown) {
      set({ error: (error as Error).message || 'Failed to update allergy', isLoading: false });
      throw error;
    }
  },

  removeAllergy: async (allergyId) => {
    const { currentPatientId } = get();
    if (!currentPatientId) return;

    set({ isLoading: true, error: null });
    try {
      const updated = await medicalHistoryApi.removeAllergy(currentPatientId, allergyId);
      set({ medicalHistory: updated, isLoading: false });
    } catch (error: unknown) {
      set({ error: (error as Error).message || 'Failed to remove allergy', isLoading: false });
      throw error;
    }
  },

  // Surgeries
  addSurgery: async (surgery) => {
    const { currentPatientId } = get();
    if (!currentPatientId) return;

    set({ isLoading: true, error: null });
    try {
      const updated = await medicalHistoryApi.addSurgery(currentPatientId, surgery);
      set({ medicalHistory: updated, isLoading: false });
    } catch (error: unknown) {
      set({ error: (error as Error).message || 'Failed to add surgery', isLoading: false });
      throw error;
    }
  },

  updateSurgery: async (surgery) => {
    const { medicalHistory } = get();
    if (!medicalHistory) return;

    set({ isLoading: true, error: null });
    try {
      const updatedSurgeries = medicalHistory.surgeries.map((s) =>
        s.id === surgery.id ? surgery : s
      );
      const updated = await medicalHistoryApi.update(medicalHistory.id, {
        surgeries: updatedSurgeries,
      });
      set({ medicalHistory: updated, isLoading: false });
    } catch (error: unknown) {
      set({ error: (error as Error).message || 'Failed to update surgery', isLoading: false });
      throw error;
    }
  },

  removeSurgery: async (surgeryId) => {
    const { currentPatientId } = get();
    if (!currentPatientId) return;

    set({ isLoading: true, error: null });
    try {
      const updated = await medicalHistoryApi.removeSurgery(currentPatientId, surgeryId);
      set({ medicalHistory: updated, isLoading: false });
    } catch (error: unknown) {
      set({ error: (error as Error).message || 'Failed to remove surgery', isLoading: false });
      throw error;
    }
  },

  // Family history
  addFamilyHistoryItem: async (item) => {
    const { currentPatientId } = get();
    if (!currentPatientId) return;

    set({ isLoading: true, error: null });
    try {
      const updated = await medicalHistoryApi.addFamilyHistoryItem(currentPatientId, item);
      set({ medicalHistory: updated, isLoading: false });
    } catch (error: unknown) {
      set({ error: (error as Error).message || 'Failed to add family history item', isLoading: false });
      throw error;
    }
  },

  updateFamilyHistoryItem: async (item) => {
    const { medicalHistory } = get();
    if (!medicalHistory) return;

    set({ isLoading: true, error: null });
    try {
      const updatedHistory = medicalHistory.familyHistory.map((f) =>
        f.id === item.id ? item : f
      );
      const updated = await medicalHistoryApi.update(medicalHistory.id, {
        familyHistory: updatedHistory,
      });
      set({ medicalHistory: updated, isLoading: false });
    } catch (error: unknown) {
      set({ error: (error as Error).message || 'Failed to update family history item', isLoading: false });
      throw error;
    }
  },

  removeFamilyHistoryItem: async (itemId) => {
    const { currentPatientId } = get();
    if (!currentPatientId) return;

    set({ isLoading: true, error: null });
    try {
      const updated = await medicalHistoryApi.removeFamilyHistoryItem(currentPatientId, itemId);
      set({ medicalHistory: updated, isLoading: false });
    } catch (error: unknown) {
      set({ error: (error as Error).message || 'Failed to remove family history item', isLoading: false });
      throw error;
    }
  },

  // Notes
  updateNotes: async (notes) => {
    const { medicalHistory, currentPatientId } = get();
    if (!currentPatientId) return;

    set({ isLoading: true, error: null });
    try {
      if (medicalHistory) {
        const updated = await medicalHistoryApi.update(medicalHistory.id, { notes });
        set({ medicalHistory: updated, isLoading: false });
      } else {
        const created = await medicalHistoryApi.create({ patientId: currentPatientId, notes });
        set({ medicalHistory: created, isLoading: false });
      }
    } catch (error: unknown) {
      set({ error: (error as Error).message || 'Failed to update notes', isLoading: false });
      throw error;
    }
  },

  // Review
  markReviewed: async () => {
    const { medicalHistory } = get();
    if (!medicalHistory) return;

    set({ isLoading: true, error: null });
    try {
      const updated = await medicalHistoryApi.markReviewed(medicalHistory.id);
      set({ medicalHistory: updated, isLoading: false });
    } catch (error: unknown) {
      set({ error: (error as Error).message || 'Failed to mark as reviewed', isLoading: false });
      throw error;
    }
  },
}));
